import MarketContextAPI from '@/api/market-context.api.js'
import formatCurrency from '@/utils/formatCurrency'

// POPULATION, first, because everything below inherits it: as of 2026-09-04 the winning-bid
// distribution these tiers are scored against is measured ONLY over auctions a call actually
// arrived on. Winning an auction and receiving a call are different events - a campaign can
// win an auction the seller never routed a call into - and the exchange now counts only the
// second kind.
//
// ⚠ THE USER-FACING VOCABULARY DID NOT CHANGE WITH IT, and that is an owner decision, not an
// oversight. On this UI a WINNING BID MEANS A DELIVERED CALL - that is the term's definition
// here. So every string below still says "winning bid" / "winning bids", and a future change
// must not rename them to "delivered calls" or "calls that came through": that would create a
// second vocabulary for one concept and strand the buyers, docs and support macros that
// already speak the first. What narrowed is the measurement; what it is called is unchanged.
//
// marketActivityLevels below is a genuinely DIFFERENT population - the ungated bidder census,
// because a competitor's posted price is real whether or not the seller delivered. See
// docs/MARKET_CONTEXT_DELIVERED_CALLS.md.
//
// Bid tiers classify a campaign's POSITIONED bid - its configured max bid for a Static
// campaign, its static bid amount for a fixed-bid Programmatic one, its median observed bid
// for an RTB or parsed-bid Programmatic one, which prices every ping itself - against the winning-bid distribution in its category + country over the market
// window:
//   Leading      - at or above P75
//   Competitive  - between P50 and P75
//   BelowMarket  - between P25 and P50
//   PricedOut    - below P25
//
// That window is NOT a fixed 24 hours. The endpoint reads a trailing 24 hours on six days of
// the week and a trailing 7 days on Mondays, because a Monday 24h window looks back at
// Sunday and this marketplace's weekend volume dip makes the percentiles above swing on a
// handful of auctions. It reports the length it used as windowHours, and the UI labels every
// number with it - see formatMarketWindow below and docs/MARKET_CONTEXT_MONDAY_WINDOW.md.
// The direct consequence: the same campaign, with the same bid, can carry a different tier
// on Monday than on Tuesday.
//
// A row's tier is null in two cases, told apart by positionedBidInUSD: a dynamic campaign that
// placed no bids (no positioned bid either), and, from the 2026-10-30 follow-ups, a bid with too
// few delivered calls from other buyers to position it against: under 30 in the row's window and
// in the last 7 days (R55, R57). The second row has no quartiles either, and is not below market.
export const bidTiers = {
  Leading: 'Leading',
  Competitive: 'Competitive',
  BelowMarket: 'BelowMarket',
  PricedOut: 'PricedOut'
}

export function getBadgeVariantForBidTier(tier) {
  switch (tier) {
  case bidTiers.Leading:
    return 'success'
  case bidTiers.Competitive:
    return 'info'
  case bidTiers.BelowMarket:
    return 'warning'
  case bidTiers.PricedOut:
    return 'danger'
  default:
    return 'secondary'
  }
}

export function getBidTierText(tier) {
  switch (tier) {
  case bidTiers.Leading:
    return 'Leading'
  case bidTiers.Competitive:
    return 'Competitive'
  case bidTiers.BelowMarket:
    return 'Below Market'
  case bidTiers.PricedOut:
    return 'Priced Out'
  default:
    return ''
  }
}

// How contested the category is over the last 7 days, as a qualitative label instead of a
// buyer count: a count, even a fuzzed one, tells a buyer how many competitors they face. UNGATED:
// a buyer bidding here is competition whether or not the seller sent a call into the auction they
// won, so this label answers "how busy" and never "how many calls got through". Null (an empty market cell) renders as empty and the
// panel hides the line - and note the panel hides ENTIRELY when there are no delivered calls,
// so a busy cell with nothing delivered never gets to show a "Highly competitive market"
// label with no prices beside it.
export const marketActivityLevels = {
  Quiet: 'Quiet',
  Active: 'Active',
  HighlyCompetitive: 'HighlyCompetitive'
}

export function getMarketActivityText(activity) {
  switch (activity) {
  case marketActivityLevels.Quiet:
    return 'Quiet market'
  case marketActivityLevels.Active:
    return 'Active market'
  case marketActivityLevels.HighlyCompetitive:
    return 'Highly competitive market'
  default:
    return ''
  }
}

// Share of last-7-day winning bids a given bid would have beaten, read as a step lookup
// off the endpoint's winRateCurve ECDF — the SAME curve the backend evaluates the tier
// cards' win rates from, so the two numbers can never disagree on one panel.
//
// Since 2026-09-04 the curve counts only auctions a call arrived on, so the denominator here
// is narrower than it used to be — the same number of winning bids the rest of this panel
// describes, since on this UI a winning bid means a delivered call. The wording stays
// "beaten ... of winning bids" for that reason. Contract:
// bidLevels sit on a CENT grid (each level is the winning bid rounded to the cent, half away
// from zero, in dollars: a $40.5059 winner sits at 40.51, so a typed $40.50 does not beat it),
// and both sides are compared in cents: a level as Math.round(level * 100), the bid as
// bid * 100 rounded to 4 decimal places, which removes float noise (19.99 * 100 is
// 1998.9999999999998) without snapping a sub-cent bid onto a cent. The compare is
// INCLUSIVE, so a tie with a winning bid counts as beating it. The C# helper
// (BuyerAnalyticsService.WinRateAt) does exactly the same, and Math.round on the share
// rounds half up like its MidpointRounding.AwayFromZero. A drift here shows up as a
// disagreement at exactly the tier bids. (Until 2026-10 the levels were whole-dollar
// ceil(winning bid) and the bid was read as Math.ceil(bid).)
export function estimateBeatsPercent(bid, categoryMarketContext) {
  const curve = categoryMarketContext?.winRateCurve

  if (!bid || !curve || !curve.bidLevels?.length) {
    return null
  }

  const bidCents = toBidCents(bid)

  // Last level <= the bid, in cents; levels are ascending
  let lastIndex = -1
  for (let i = 0; i < curve.bidLevels.length && toCents(curve.bidLevels[i]) <= bidCents; i++) {
    lastIndex = i
  }

  if (lastIndex === -1) {
    return 0
  }

  return Math.round(curve.cumulativeShares[lastIndex] * 100)
}

// Two bids are "the same bid" when they render as the same currency string, so equality tests
// on bids run on cents rather than on raw dollars
export function toCents(bid) {
  return Math.round(bid * 100)
}

// A typed bid in cents, to 4 decimal places: rid of float noise (19.99 * 100 is
// 1998.9999999999998) without snapping a sub-cent bid onto a cent. The bid side of every
// inclusive compare against the cent grid, as WinRateAt reads it in C#
function toBidCents(bid) {
  return Math.round(bid * 100 * 10000) / 10000
}

// ---------------------------------------------------------------------------
// Market intelligence, suggested bid iteration 1, behind rx-market-intelligence
// (docs/MARKET_CONTEXT_SUGGESTED_BID_ITERATION_1.md, §3 and §5).
//
// From ExchangeAnalyticsService's backend PR 1, E2 adds a SUGGESTED SETUP, a bid and a minimum
// call length, priced on one scope's delivered calls at its standard minimum over the last 7 days.
// Every field the panel reads keeps its meaning (all minimums, at the requested cell); the card
// reads `suggestion`, `scope` and `insufficientData`. The helpers below word what the card, and
// the campaigns list's suggestions for existing campaigns, show of it. The copy lives here rather
// than in the components, as the tooltips' does further down.
//
// Every number they name is per call, never per campaign, and none is a price per call (R21).
// ---------------------------------------------------------------------------

// Whether an E2 response carries the card's fields. Backend PR 1 always sends insufficientData, a
// boolean that is never null, so a response without it comes from an older backend: the card has
// nothing to show then, and the budget step keeps today's panel.
export function hasMarketIntelligence(context) {
  return typeof context?.insufficientData === 'boolean'
}

// Where a typed bid sits on the card (R3): E1's tier scale renamed, read on the suggestion's own
// calls. Below market covers PricedOut and BelowMarket, Highly competitive is Leading.
export const bidPositions = {
  BelowMarket: 'BelowMarket',
  Competitive: 'Competitive',
  HighlyCompetitive: 'HighlyCompetitive'
}

export function getBidPositionText(position) {
  switch (position) {
  case bidPositions.BelowMarket:
    return 'Below market'
  case bidPositions.Competitive:
    return 'Competitive'
  case bidPositions.HighlyCompetitive:
    return 'Highly competitive'
  default:
    return ''
  }
}

// The same colours as the tier badge on the campaigns list, position for tier
export function getBadgeVariantForBidPosition(position) {
  switch (position) {
  case bidPositions.BelowMarket:
    return 'warning'
  case bidPositions.Competitive:
    return 'info'
  case bidPositions.HighlyCompetitive:
    return 'success'
  default:
    return 'secondary'
  }
}

// A flat cell (R30): P25 and P75 on one cent, so the market is one price. The backend sends it as
// isFlat; the cents are compared here too, so the card never draws a range where there is one price
export function isFlatSuggestion(suggestion) {
  return Boolean(suggestion)
    && (suggestion.isFlat === true || toCents(suggestion.winningBidP25InUSD) === toCents(suggestion.winningBidP75InUSD))
}

// How far below the suggested bid the Competitive range starts when P50 and P75 share a cent (I36;
// owner, 2026-10-30): $5.00, in cents like everything compared here
const COLLAPSED_RANGE_SPAN_CENTS = 500

// The two cards the Competitive range runs between, either of them undefined without it
function findRangeCards(suggestion) {
  const tiers = suggestion?.tiers || []

  return {
    competitive: tiers.find(tier => tier.key === 'Competitive'),
    frontRunner: tiers.find(tier => tier.key === 'FrontRunner')
  }
}

// P50 and P75 on one cent with P25 below them (I36; owner, 2026-10-30), which R30's flat rule
// (P25 = P75) does not cover, so no flat suggestion is this case. Read from the percentiles: since
// the Front-runner card sits above P90 (R61) the Competitive and Front-runner cards never land on
// one price, so the collapse is a fact of the market, not of the cards.
function isCollapsedSuggestion(suggestion) {
  if (!suggestion || isFlatSuggestion(suggestion)) {
    return false
  }

  return toCents(suggestion.winningBidP50InUSD) === toCents(suggestion.winningBidP75InUSD)
}

// Where Competitive starts in a collapsed suggestion, in cents: $5.00 under P75, and never under
// P25. A normal cell's range starts one step above where its Competitive does (P50 + 5c over P50),
// and the range here keeps that offset from this start, so the position agrees with the range. With
// P25 < P50 = P75 the start stays under P75, so the range's low end stays under its high end.
function getCollapsedCompetitiveStartCents(suggestion) {
  return Math.max(
    toCents(suggestion.winningBidP75InUSD) - COLLAPSED_RANGE_SPAN_CENTS,
    toCents(suggestion.winningBidP25InUSD)
  )
}

// The Competitive range (R1): from the Competitive card to the Front-runner card, so it names no
// price the cards do not offer. Null without both cards. Where P50 and P75 share a cent (I36;
// owner, 2026-10-30) the Competitive card sits on the market's one upper price, so the range
// starts $5.00 under P75 instead, never under the Entry card (P25 plus the step): the one place it
// names a price no card offers, "$45.05" under a $52.50 card. No card is added for it.
export function getCompetitiveRange(suggestion) {
  const { competitive, frontRunner } = findRangeCards(suggestion)

  if (!competitive || !frontRunner) {
    return null
  }

  if (isCollapsedSuggestion(suggestion)) {
    // The Competitive card's step over its percentile, 5c, is the offset that puts the range's
    // start one step above where Competitive starts (the Front-runner card carries a different
    // margin, over P90, R61)
    const stepCents = toCents(competitive.bidInUSD) - toCents(suggestion.winningBidP50InUSD)

    return {
      lowInUSD: (getCollapsedCompetitiveStartCents(suggestion) + stepCents) / 100,
      highInUSD: frontRunner.bidInUSD
    }
  }

  return { lowInUSD: competitive.bidInUSD, highInUSD: frontRunner.bidInUSD }
}

// Where a typed bid sits (R3), per keystroke, against the suggestion's own P50 and P75: Below
// market under P50, Competitive from P50 up to (not including) P75, Highly competitive at P75 or
// more. In cents, a tie counting upward, as estimateBeatsPercent counts one. A flat cell has two
// positions only (R30): Below market under its one price, Competitive at or above it, never Highly
// competitive. Where P50 and P75 share a cent below a lower P25 (I36; owner, 2026-10-30) Competitive
// starts $5.00 under P75 instead, never under P25, to agree with the range the card names, so $44.99
// is Below market, $45.00 to $49.99 Competitive and $50.00 Highly competitive in a P25 $20 / P50 =
// P75 $50 cell. Null without a bid or a suggestion.
export function getPositionForBid(bid, suggestion) {
  if (!bid || !suggestion) {
    return null
  }

  const bidCents = toBidCents(bid)

  if (isFlatSuggestion(suggestion)) {
    return bidCents >= toCents(suggestion.winningBidP75InUSD) ? bidPositions.Competitive : bidPositions.BelowMarket
  }

  if (bidCents >= toCents(suggestion.winningBidP75InUSD)) {
    return bidPositions.HighlyCompetitive
  }

  const competitiveStartCents = isCollapsedSuggestion(suggestion)
    ? getCollapsedCompetitiveStartCents(suggestion)
    : toCents(suggestion.winningBidP50InUSD)

  return bidCents >= competitiveStartCents ? bidPositions.Competitive : bidPositions.BelowMarket
}

// R59 (owner, 2026-10-30; docs/MARKET_CONTEXT_SUGGESTED_BID_REPLAY_FOLLOWUPS.md F7) and R62 (owner,
// 2026-10-31): a campaign with no market suggestion that loses on conversion rather than on price.
// The exchange ranks on bid x conversion, so a step up the bid alone does not close a gap conversion
// opened: North Star's FE Transfers lost 21 of 626 contested auctions, every one to a higher
// conversion, and needed $65.08 against its $47 to win 19 of them. R59 told such a campaign to better
// its conversion rate and nothing about its bid. From R62 E1 may hand it a setup that compensates, a
// bid up to 150% of its own and/or a shorter minimum (`suggestedSetup` on the row, the shape of any
// other row's), which the list shows as a chip or as advice and the card as its setup, with
// CONVERSION_ADVICE_SENTENCE as the note on why. Where E1 has no setup to give, the row says only
// "Improve the conversion rate", and its hover adds CONVERSION_LEVERS_SENTENCE.
//
// The sentence is about the campaign alone, and shared by the list's hover and the card so the two
// never word it apart. Buyer-facing copy never explains why another buyer won an auction (the
// owner's rule of 2026-10-31), so there is no rival, no rate, no score and no count in it, and no
// number derived from conversion (R18). E1 sends the state as `losesOnConversion`, only on a row with
// no market suggestion
export const CONVERSION_ADVICE_SENTENCE = 'Your conversion rate counts against your bid in these auctions.'

// The two levers the buyer holds, where there is no setup to act on (R62)
export const CONVERSION_LEVERS_SENTENCE = 'Answering more calls, or keeping callers on the line longer, raises your rank at the same bid.'

export function getConversionAdviceText() {
  return 'Improve the conversion rate'
}

export function getConversionAdviceTooltip() {
  return `${CONVERSION_ADVICE_SENTENCE} ${CONVERSION_LEVERS_SENTENCE}`
}

// The sentence under the position (the source plan's §4.2 templates). With losesOnConversion (R59)
// the conversion sentence takes the place of the market's raise under Below market, and follows the
// position sentence above it: the market's raise is never named for that campaign. Where the card
// already carries the note under the campaign's own setup (R62), `withNote` false leaves it out here,
// so one card never says it twice; the market's raise stays out all the same
export function getPositionText(position, suggestion, { losesOnConversion = false, withNote = true } = {}) {
  const note = losesOnConversion && withNote ? CONVERSION_ADVICE_SENTENCE : ''
  const withNoteAfter = sentence => [sentence, note].filter(Boolean).join(' ')

  switch (position) {
  case bidPositions.BelowMarket: {
    const range = getCompetitiveRange(suggestion)
    const increase = losesOnConversion
      ? note
      : `Increasing to ${formatCurrency(suggestion.bidInUSD)} may improve access to available volume.`

    // A flat cell's one price is already named on the range line, and a range from a price to
    // itself is not a range
    const rangeText = range && !isFlatSuggestion(suggestion)
      ? `Competitive range is ${formatCurrency(range.lowInUSD)}–${formatCurrency(range.highInUSD)}.`
      : ''

    return [rangeText, increase].filter(Boolean).join(' ')
  }
  // Not "within the competitive range" (I7): a bid exactly on P50, $37.00, sits below the range's
  // first price, $37.05
  case bidPositions.Competitive:
    return withNoteAfter('Your bid is competitive in this market.')
  case bidPositions.HighlyCompetitive:
    return withNoteAfter('Your bid is positioned to compete strongly for available inventory.')
  default:
    return ''
  }
}

// The article before a count of seconds, as it is spoken: "an 8-second minimum", "an 11-second",
// "an 18-second", "an 89-second", "a 90-second". The prod replay of 2026-10-31 (version 9) served an
// 89-second minimum and read "a 89-second minimum"
export function secondsArticle(seconds) {
  const n = Number(seconds)
  const startsWithVowelSound = n === 8 || n === 11 || n === 18
    || (n >= 80 && n <= 89) || (n >= 800 && n <= 899) || (n >= 8000 && n <= 8999)

  return startsWithVowelSound ? 'an' : 'a'
}

// Which calls a number describes (I12), as the phrase after "winning bids": one exact minimum, a
// bucket of them, or, where nothing was priced (pricedOn null: "No suggested bid yet", and E1's rows
// in a cell without a suggestion, I4), every minimum. A CPA-only category's calls (AllMinimums) have
// no minimum to name, so its phrase is empty.
export function getMinimumText(pricedOn) {
  if (!pricedOn) {
    return 'at all minimums'
  }

  const { basis, minSeconds, maxSeconds } = pricedOn

  switch (basis) {
  case 'Exact':
    return `at ${secondsArticle(minSeconds)} ${minSeconds}-second minimum`
  case 'Bucket':
    if (maxSeconds == null) {
      return `at minimums of ${minSeconds} seconds or longer`
    }

    // The 1–29 s bucket: "from 1" reads as a typo for the shortest minimum there is
    return minSeconds <= 1
      ? `at minimums under ${maxSeconds + 1} seconds`
      : `at minimums of ${minSeconds} to ${maxSeconds} seconds`
  default:
    return ''
  }
}

// A flat cell's range line (R30): its one price, which every quartile of the pricing calls sits on,
// with the calls named (I12): "Winning bids here were $35.00 at a 90-second minimum"
export function getFlatMarketText(suggestion) {
  const minimum = getMinimumText(suggestion.pricedOn)

  return `Winning bids here were ${formatCurrency(suggestion.winningBidP75InUSD)}${minimum ? ` ${minimum}` : ''}`
}

// "$50.05 / 90 sec", or the bid alone where the suggestion names no minimum (a CPA-only category)
export function getSuggestedSetupText({ bidInUSD, minimumSeconds }) {
  return minimumSeconds == null
    ? formatCurrency(bidInUSD)
    : `${formatCurrency(bidInUSD)} / ${minimumSeconds} sec`
}

// The bucket a minimum's reason names, as the phrase after "buyers with"
function getBucketMinimumText({ minSeconds, maxSeconds }) {
  if (maxSeconds == null) {
    return `a minimum of ${minSeconds} seconds or longer`
  }

  return minSeconds <= 1
    ? `a minimum under ${maxSeconds + 1} seconds`
    : `${secondsArticle(minSeconds)} ${minSeconds}-to-${maxSeconds}-second minimum`
}

// The suggestion the budget step's card offers, whatever bid is typed: the campaign's own row's setup
// for a campaign losing on conversion (R62), which the card leads with instead of the market's,
// otherwise the market's suggestion. A CPA-only category's is a bid alone (I5). Null without one.
// Not the setup on screen, which follows the typed bid (R58). The usage events compare a saved bid
// and minimum with it (docs/MARKET_CONTEXT_USAGE_ANALYTICS.md)
export function getOfferedSuggestion({ suggestion, marketPosition, isCpaOnly = false }) {
  const offered = marketPosition?.losesOnConversion === true ? marketPosition.suggestedSetup : suggestion

  if (!offered) {
    return null
  }

  return { bidInUSD: offered.bidInUSD, minimumSeconds: isCpaOnly ? null : (offered.minimumSeconds ?? null) }
}

// Why the suggested minimum (I14; R39), in R15's one template: the share of the scope's delivered
// calls in the minimum's bucket, to the nearest tenth, with the bucket's range named. Never "most",
// never an account count. Capped at 9 in 10 (I44), since a share of 95% or more would otherwise
// read "10 in 10", which overstates it, and a share under 5% reads "Fewer than 1 in 10" rather than
// "0 in 10".
//
// Empty without a bucket. A CPA-only category has none (it has no minimum), and neither has a
// standard the category's longest minimum capped (I6): the suggested minimum is then the cap, and
// the bucket's calls would give a reason for a minimum the card does not suggest.
export function getMinimumReasonText(bucket) {
  if (!bucket || typeof bucket.callsShare !== 'number') {
    return ''
  }

  const tenths = Math.min(9, Math.round(bucket.callsShare * 10))
  const share = tenths < 1 ? 'Fewer than 1 in 10' : `${tenths} in 10`

  return `${share} calls here go to buyers with ${getBucketMinimumText(bucket)}.`
}

// The market that served the card (R26), named the way the buyer picked it: the requested cell,
// or the broader rung of the ladder its calls came from. The ids stand in for a name not given.
export function getScopeText(scope, { categoryName = '', countryName = '', languageName = '' } = {}) {
  const country = countryName || scope?.countryId || ''
  const language = languageName || scope?.languageId || ''

  switch (scope?.level) {
  case 'Category':
    return `${categoryName} · all countries and languages`
  case 'Country':
    return [categoryName, country, 'all languages'].filter(Boolean).join(' · ')
  default:
    return [categoryName, country, language].filter(Boolean).join(' · ')
  }
}

// The market a campaigns-list row was positioned in when it fell back to the last 7 days (§3.7):
// E2's 7 days of the scope that served the standard minimum, which is a broader rung of the ladder
// when the cell itself was too thin there (I38). On its own window a row is always its own cell's.
export function getScopeMarketText(scope) {
  switch (scope?.level) {
  case 'Country':
    return 'in this category and country'
  case 'Category':
    return 'in this category'
  default:
    return 'in this market'
  }
}

// The guidance for a campaign whose terms are not typed into the budget step (§3.6, §3.9), for the
// card and the campaigns list alike, worded for where its terms come from (I16). A RingbaRTB bidder
// returns both. A Programmatic campaign fixes each term in its settings by default and parses it
// from its API's response when told to, so each term gets its own clause. In a CPA-only category a
// Programmatic campaign's bid is forced static and there is no minimum, so it is told the bid alone
// (I42). No trailing period: the line is copied as an instruction.
export function getSetupGuidanceText({
  routingType,
  bidInUSD,
  minimumSeconds = null,
  isBidDynamic = false,
  isCallLengthDynamic = false,
  isCpaOnly = false
}) {
  const bid = formatCurrency(bidInUSD)

  if (routingType !== 'Programmatic') {
    return minimumSeconds == null ? `Return ${bid}` : `Return ${bid} with ${secondsArticle(minimumSeconds)} ${minimumSeconds}-second minimum`
  }

  const returnsBid = isBidDynamic && !isCpaOnly

  if (minimumSeconds == null || isCpaOnly) {
    return returnsBid ? `Return ${bid}` : `Set the static bid to ${bid}`
  }

  if (returnsBid && isCallLengthDynamic) {
    return `Return ${bid} with ${secondsArticle(minimumSeconds)} ${minimumSeconds}-second minimum`
  }

  if (returnsBid) {
    return `Return ${bid} and set the call length to ${minimumSeconds} seconds`
  }

  return isCallLengthDynamic
    ? `Set the static bid to ${bid} and return ${secondsArticle(minimumSeconds)} ${minimumSeconds}-second minimum`
    : `Set the static bid to ${bid} and the call length to ${minimumSeconds} seconds`
}

// ---------------------------------------------------------------------------
// The campaigns list's suggestions for existing campaigns (Slice H: the plan's §3.9 and §4.6),
// behind rx-market-intelligence. From backend PR 2 every E1 row carries what its campaign bids now
// (currentTerms), and a campaign the trigger picked carries its suggestion (suggestedSetup). The
// contested counts that picked it stay server-side: the wire still carries them (contested), and
// the client does not read them. The only count a buyer sees is the row's own Missed count, in the
// Missed column's definition and window (R54). No copy here says what a suggestion would have won,
// names a score or a rival, turns an auction into a call (I25), or prices a call (R21).
// ---------------------------------------------------------------------------

// Whether a row comes from backend PR 2 on. Its currentTerms is always on the wire, null when the
// reads behind it failed; an older backend's row has no such key, and the list shows it as today.
export function hasSuggestionFields(position) {
  return position != null && position.currentTerms !== undefined
}

// A Static campaign's own minimum, as E1 reads it (§3.9): its Budget's minimum when one is
// selected, none otherwise, which pays on every connected call
export function getConfiguredMinimumSeconds(campaign) {
  const budget = campaign?.budget

  return budget?.minimumDurationSelected === true && Number(budget.minimumDurationInSeconds) > 0
    ? Number(budget.minimumDurationInSeconds)
    : null
}

const formatCount = count => Number(count).toLocaleString('en-US')

// The direction of cost on a change of minimum (I18): a shorter minimum pays on more calls; a
// longer one, or one set where there was none, on fewer
export function getMinimumCostText(fromSeconds, toSeconds) {
  if (toSeconds == null || fromSeconds === toSeconds) {
    return ''
  }

  return fromSeconds != null && toSeconds < fromSeconds ? 'You then pay on more calls.' : 'You then pay on fewer calls.'
}

// What the campaign bids now, the own-bid line beside the badge (§3.9, §4.6):
// - Static: "Your bid $30.00 · 120 s", or "· no minimum" (it pays on every connected call);
// - RingbaRTB and Programmatic, fixed or parsed terms alike: "Bids $25.00 at 90 s", or "· minimum
//   not reported" when neither its pings nor its won auctions carried one (I23). One wording for
//   both (owner, 2026-10-30), in place of the three earlier per-routing subjects (the bidder, the
//   API, the static bid); the advice beside it still follows a Programmatic campaign's settings (I16).
// A CPA-only category has no minimum, so its line names the bid alone. `varies` adds "(varies)" for
// a bidder whose minimums the tooltip breaks down (I23).
export function getCurrentTermsText(terms, { routingType, isCpaOnly = false, varies = false } = {}) {
  const bid = formatCurrency(terms.bidInUSD)
  const subject = routingType === 'RingbaRTB' || routingType === 'Programmatic' ? 'Bids' : 'Your bid'

  let minimum = ''

  if (!isCpaOnly) {
    if (terms.minimumSeconds != null) {
      minimum = routingType === 'Static' ? ` · ${terms.minimumSeconds} s` : ` at ${terms.minimumSeconds} s`
    } else {
      minimum = terms.source === 'Configuration' ? ' · no minimum' : ' · minimum not reported'
    }
  }

  return `${subject} ${bid}${minimum}${varies ? ' (varies)' : ''}`
}

// I23: the per-minimum breakdown for a bidder that returns more than one minimum, as a tooltip:
// "This campaign varies its minimum: $25.00 at 90 s on 61.5% of its pings; $30.00 at 120 s on
// 38.5% of its pings." One wording for RingbaRTB and Programmatic, as the own-bid line's (owner,
// 2026-10-30): the subject is the campaign, never its bidder or its API. Empty without one. Only
// a bidder that prices its own pings has one: E1 sends it for terms read off pings, which a
// fixed-bid Programmatic campaign's never are.
export function getTermsBreakdownText(byMinimum) {
  if (!byMinimum?.length) {
    return ''
  }

  const entries = byMinimum.map(entry => `${formatCurrency(entry.bidInUSD)} at ${entry.minimumSeconds} s on ${entry.sharePercent}% of its pings`)

  return `This campaign varies its minimum: ${entries.join('; ')}.`
}

// A Programmatic campaign's bid and minimum as fixed in its settings: the bid unless its API returns
// one (a CPA-only category forces it static, I42), the call length unless its API returns that, when
// it is set. Null without a fixed bid. `returnsMinimum` says the API returns the call length, which
// is then unknown here rather than none. The amount can arrive as a currency string ("$1,250.00"), as
// convertCurrencyStringToNumber in campaign.store reads it; that store is not imported here
export function getConfiguredProgrammaticTerms(routingType, bidSettings, { isCpaOnly = false } = {}) {
  if (routingType !== 'Programmatic' || !bidSettings) {
    return null
  }

  const returnsBid = bidSettings.isBidDynamic === true && !isCpaOnly
  const bidInUSD = returnsBid || bidSettings.staticBidAmount == null
    ? null
    : (Number(String(bidSettings.staticBidAmount).replace(/[$,]/g, '')) || null)

  if (!bidInUSD) {
    return null
  }

  const returnsMinimum = bidSettings.conversionCallLengthDynamic === true
  const callLength = returnsMinimum ? null : Number(bidSettings.conversionCallLengthInSeconds)

  return { bidInUSD, minimumSeconds: callLength > 0 ? callLength : null, returnsMinimum }
}

// What the campaigns list's Bid column shows for a RingbaRTB or Programmatic campaign (owner,
// 2026-11-01, R65): what E1 says it bids now (currentTerms, backend PR 2 on), else a Programmatic
// campaign's fixed bid from its settings. Null where there is no number, and the column reads
// "Dynamic" as before. `source` is the wire's (Configuration, Pings or WonAuctions)
export function getBidColumnTerms({ marketPosition, routingType, programmaticBidSettings, isCpaOnly = false }) {
  const reported = marketPosition?.currentTerms

  if (reported?.bidInUSD != null) {
    return {
      bidInUSD: reported.bidInUSD,
      minimumSeconds: reported.minimumSeconds ?? null,
      source: reported.source || null,
      byMinimum: reported.byMinimum || [],
      returnsMinimum: false
    }
  }

  const configured = getConfiguredProgrammaticTerms(routingType, programmaticBidSettings, { isCpaOnly })

  return configured ? { ...configured, source: 'Configuration', byMinimum: [] } : null
}

// The Bid column's second line under the bid: "at 90 s"; "no minimum" for a minimum set nowhere,
// as the own-bid line words it; "minimum not reported" when neither its pings nor its won auctions
// carried one (I23). Nothing in a CPA-only category, which has no minimum, nor for a Programmatic
// campaign read off its settings whose API returns the call length, which this page cannot know
export function getBidTermsMinimumText(terms, { isCpaOnly = false } = {}) {
  if (isCpaOnly || terms.returnsMinimum) {
    return ''
  }

  if (terms.minimumSeconds != null) {
    return `at ${terms.minimumSeconds} s`
  }

  return terms.source === 'Configuration' ? 'no minimum' : 'minimum not reported'
}

// The Bid column's hover: the split for a campaign that varies its minimum (I23's sentence), where
// the terms came from otherwise. The subject is the campaign, never its bidder or its API, as the
// own-bid line's. `marketWindow` is the list's, its phrase left out while unknown. A CPA-only
// category has no minimum, so its hover names none, as its cell shows none
export function getBidTermsTooltip(terms, marketWindow = {}, { isCpaOnly = false } = {}) {
  const breakdown = isCpaOnly ? '' : getTermsBreakdownText(terms.byMinimum)

  if (breakdown) {
    return breakdown
  }

  if (terms.source === 'Configuration') {
    return 'Set in this campaign\'s settings'
  }

  const windowPhrase = marketWindow.long ? ` in the ${marketWindow.long}` : ''

  return terms.minimumSeconds != null && !isCpaOnly
    ? `Typical bid and minimum this campaign returned${windowPhrase}`
    : `Typical bid this campaign returned${windowPhrase}`
}

// E2's window, in hours: always the last 7 days, unlike E1's
export const MARKET_CONTEXT_WINDOW_HOURS = 168

// Renders the market window the E1 rows were measured over, in the two lengths the UI needs:
// `short` for a column header, `long` for a sentence. The single source of this label - there
// is no other data-driven time-window string in the app and no duration helper in src/utils -
// so both live here beside getBidTierText, which the same components already import.
//
// An unknown window (the fetch has not resolved) deliberately renders as EMPTY STRINGS rather
// than defaulting to 24h. Defaulting would render `Missed (24h)` and then flip to
// `Missed (7d)` a moment later - a visible flash, on Mondays, on the exact label added to
// stop people misreading these numbers. Callers render the label bare in that case.
export function formatMarketWindow(hours) {
  if (!hours) {
    return { short: '', long: '' }
  }

  return hours >= 168
    ? { short: '7d', long: 'last 7 days' }
    : { short: '24h', long: 'last 24 hours' }
}

// What a Static campaign's suggestion chip does, the first sentence of its hover (R54): "Raises the
// bid to $50.05" for a raise that keeps the minimum; "Switches to $50.05 with a 90-second minimum"
// when the bid and the minimum change, or a minimum is set where there was none; "Changes the
// minimum to 90 seconds" when the bid stays (I19). `currentTerms` is the row's, which says whether
// the bid stays. No trailing period: the chip's hover ends the sentence.
export function getSuggestionActionText(setup, currentTerms) {
  if (!setup) {
    return ''
  }

  const bid = formatCurrency(setup.bidInUSD)

  if (setup.changesMinimum !== true || setup.minimumSeconds == null) {
    return `Raises the bid to ${bid}`
  }

  const keepsBid = currentTerms?.bidInUSD != null && toCents(setup.bidInUSD) <= toCents(currentTerms.bidInUSD)

  return keepsBid
    ? `Changes the minimum to ${setup.minimumSeconds} seconds`
    : `Switches to ${bid} with ${secondsArticle(setup.minimumSeconds)} ${setup.minimumSeconds}-second minimum`
}

// The one count a buyer sees beside a suggestion (R54): the row's own Missed count, so the chip's
// hover, the advice's hover, the confirmation and the Missed column can never disagree. Its
// definition is the column's (auctions lost to a higher bid from another account, R44) and so is
// its window, from formatMarketWindow: "This campaign lost 7,244 auctions to higher bids from other
// buyers in the last 24 hours." The wording follows the banner's (R38). `subject` is "It" in a
// confirmation that has just named the campaign.
//
// Empty at 0, since "lost 0 auctions" under a raise would undercut it (a chip is triggered on 7 days
// of contested auctions, so a campaign paused today can carry one with nothing missed), and before
// the window is known, for the same reason the column's label is bare then. It never says the
// suggestion would win those auctions back: Missed counts losses to a higher bid, and the suggested
// bid does not beat every one of them.
export function getMissedAuctionsText(missedCalls, marketWindow = {}, { subject = 'This campaign' } = {}) {
  if (!missedCalls || !marketWindow?.long) {
    return ''
  }

  const lost = Number(missedCalls) === 1
    ? '1 auction to a higher bid from another buyer'
    : `${formatCount(missedCalls)} auctions to higher bids from other buyers`

  return `${subject} lost ${lost} in the ${marketWindow.long}.`
}

// ---------------------------------------------------------------------------
// The Opportunity cell without a suggestion (R60; owner, 2026-10-30; F6 in
// docs/MARKET_CONTEXT_SUGGESTED_BID_REPLAY_FOLLOWUPS.md). The column says only what the campaign can
// do, or why nothing is suggested. Until R60 a row without a chip or advice showed a rank band there,
// which ranked the bid on a different population from the badge one column over, and the two
// contradicted each other on 27 of 53 rows of the prod replay. E1 still sends `rankBand`; nothing
// here reads it.
//
// Only a market with no suggested setup gets words: "No suggested bid yet", with no hover. Any other
// positioned row with no suggestion shows a dash with no hover (R66; owner, 2026-11-02), wherever its
// bid sits: at or above the market (R13), outbid or not, it read "Outbid, no raise suggested" or "No
// change suggested", and below it "No raise suggested", each with a hover. A row with no badge (tier
// null) shows the same dash, in both columns (R64). Never a buyer count, a rank or a percentile, a
// rival's bid, rate or score, or how much evidence there is.
// ---------------------------------------------------------------------------

// Why a positioned row with a badge has no suggestion, where the cell says so
export const noSuggestionReasons = Object.freeze({
  // The market has no suggested setup: pricedOn null, E1's all-minimums path (I4)
  NoMarketSetup: 'NoMarketSetup'
})

// The reason for a positioned row with a badge in a market with no suggested setup; null for any other
// row, which shows a dash (R66). pricedOn is compared with null strictly: a row with no such key comes
// from a backend older than PR 1, which priced no setup at all, so it gets the dash rather than "No
// suggested bid yet"
export function getNoSuggestionReason(position) {
  if (position?.positionedBidInUSD == null || position.tier == null) {
    return null
  }

  return position.pricedOn === null ? noSuggestionReasons.NoMarketSetup : null
}

// What the campaigns list's Opportunity cell shows for a row (BidOpportunityCell), in the order the
// cell tries them. The list counts its rows by kind for the usage events
export const opportunityKinds = Object.freeze({
  // A Static row scored on a bid or minimum it no longer has, between an inline edit and the refetch
  Stale: 'stale',
  // Slice H's chips (§3.9): a raise that keeps the minimum, and a switch of bid and minimum
  RaiseChip: 'raise_chip',
  SwitchChip: 'switch_chip',
  // A RingbaRTB or Programmatic row's suggestion, worded for its bidder or its API
  Advice: 'advice',
  // R59, R62: losing on conversion with no setup to offer
  ConversionAdvice: 'conversion_advice',
  // Today's chip, outside the rollout or on a row from before Slice H
  LegacyChip: 'legacy_chip',
  AtSuggestedBid: 'at_suggested_bid',
  // R60: a positioned bid with no badge
  Unpositioned: 'unpositioned',
  NoSuggestion: 'no_suggestion',
  None: 'none'
})

/**
 * A row's Opportunity cell (BidOpportunityCell), from the props the cell takes: its kind (see
 * `opportunityKinds`) and what that state reads, so the cell and the list's usage events decide it
 * in one place. Slice H's suggestions show inside the rx-market-intelligence rollout
 * (`marketIntelligence`) on a row from backend PR 2 on; anything else is today's cell.
 *
 * @returns {{ kind: string, usesSuggestions: boolean, setup: object|null, currentBidInUSD: number|undefined,
 *   adviceText: string, noSuggestionReason: string|null }} the advice for `Advice` and the reason for
 *   `NoSuggestion`, empty for any other kind
 */
export function getOpportunity({
  campaign,
  marketPosition,
  isStatic = false,
  marketIntelligence = false,
  routingType = null,
  isCpaOnly = false,
  programmaticBidSettings = null
}) {
  const usesSuggestions = marketIntelligence && hasSuggestionFields(marketPosition)
  const configuredBid = campaign?.budget?.maxBidPerCallInUSD

  // The bid the row was positioned on, so a raise is measured against the number the suggestion was
  // derived from; the campaign's own only when the endpoint's row is absent
  const currentBidInUSD = marketPosition?.positionedBidInUSD ?? configuredBid

  // Only for a campaign the trigger picked: never at or above the market, never a lower bid
  const setup = usesSuggestions ? (marketPosition.suggestedSetup || null) : null

  const opportunity = (kind, details = {}) => ({
    kind,
    usesSuggestions,
    setup,
    currentBidInUSD,
    adviceText: '',
    noSuggestionReason: null,
    ...details
  })

  if (isStatic) {
    const bidMoved = marketPosition?.positionedBidInUSD != null
      && configuredBid != null
      && toCents(marketPosition.positionedBidInUSD) !== toCents(configuredBid)

    const minimumMoved = usesSuggestions
      && marketPosition.currentTerms != null
      && (marketPosition.currentTerms.minimumSeconds ?? null) !== getConfiguredMinimumSeconds(campaign)

    if (bidMoved || minimumMoved) {
      return opportunity(opportunityKinds.Stale)
    }
  }

  if (isStatic && setup) {
    if (setup.changesMinimum && setup.minimumSeconds != null) {
      return opportunity(opportunityKinds.SwitchChip)
    }

    if (toCents(setup.bidInUSD) > toCents(currentBidInUSD)) {
      return opportunity(opportunityKinds.RaiseChip)
    }
  }

  // Not for a CPA-RTB campaign, whose postback bid is a static amount the budget step leaves out too
  const adviceText = !isStatic && setup && !(routingType === 'RingbaRTB' && isCpaOnly)
    ? getSetupGuidanceText({
      routingType,
      bidInUSD: setup.bidInUSD,
      minimumSeconds: setup.minimumSeconds,
      isBidDynamic: programmaticBidSettings?.isBidDynamic === true,
      isCallLengthDynamic: programmaticBidSettings?.conversionCallLengthDynamic === true,
      isCpaOnly
    })
    : ''

  if (adviceText) {
    return opportunity(opportunityKinds.Advice, { adviceText })
  }

  // The chip and the advice come first, so this is a flagged row E1 could give no setup
  if (usesSuggestions && marketPosition.losesOnConversion === true) {
    return opportunity(opportunityKinds.ConversionAdvice)
  }

  // Today's chip, from E1's suggestedBidInUSD, and the state a campaign lands in once it acts on it.
  // A raise that is not a number (a bid that is not one) is neither, and falls through
  if (isStatic && !usesSuggestions && marketPosition?.suggestedBidInUSD != null && currentBidInUSD != null) {
    const raiseInUSD = marketPosition.suggestedBidInUSD - currentBidInUSD

    if (raiseInUSD > 0) {
      return opportunity(opportunityKinds.LegacyChip)
    }

    if (raiseInUSD <= 0) {
      return opportunity(opportunityKinds.AtSuggestedBid)
    }
  }

  if (marketPosition?.positionedBidInUSD != null && marketPosition.tier == null) {
    return opportunity(opportunityKinds.Unpositioned)
  }

  const noSuggestionReason = getNoSuggestionReason(marketPosition)

  if (noSuggestionReason) {
    return opportunity(opportunityKinds.NoSuggestion, { noSuggestionReason })
  }

  return opportunity(opportunityKinds.None)
}

// The kind alone, which the list counts its rows by for the usage events
export function getOpportunityKind(props) {
  return getOpportunity(props).kind
}

// The cell's text for each reason. "No suggested bid yet" is the budget step card's phrase for a
// market with no suggested setup, so the two surfaces word that state alike. It has no hover: buyer
// copy never says a market lacks activity or evidence (R64), and the phrase says all there is to say
export function getNoSuggestionText(reason) {
  return reason === noSuggestionReasons.NoMarketSetup ? 'No suggested bid yet' : ''
}

// ---------------------------------------------------------------------------
// Column-header and cell tooltip copy.
//
// These live here, beside getBidTierText and formatMarketWindow, rather than inline in the
// components that render them, because the copy is the part most likely to be revised. The ones
// that name a window take the formatMarketWindow result and drop the window phrase when it is
// empty, the same way missedCallsTooltip does: naming a window before the fetch resolves would
// print "last 24 hours" and then correct itself to "last 7 days" on a Monday.
// ---------------------------------------------------------------------------

// `withSevenDayFallback` is for rows from backend PR 1 on (they carry positionWindowHours): E1 then
// positions a row on its own window only when that window holds 30 calls at the market's standard
// minimum, and on the last 7 days otherwise (§3.7), so one header covers rows of both windows
// (I38). Each row's own tooltip names the window it used. On a Monday the window is 7 days anyway.
// The phrase says which rows, not why: buyer copy never calls a market thin (R64)
export function getMarketPositionTooltip(marketWindow = {}, { withSevenDayFallback = false } = {}) {
  const fallbackPhrase = withSevenDayFallback && marketWindow.short !== '7d' ? ', or the last 7 days for some campaigns' : ''
  const windowPhrase = marketWindow.long ? ` over the ${marketWindow.long}${fallbackPhrase}` : ''

  // No Static-vs-dynamic clause: the indicator's own line already labels the marker
  // `Your bid` or `Your typical bid` depending on routing type.
  //
  // Note "auctions actually sold for" now means auctions a call arrived on, which is the narrower
  // set the whole panel is scored against - the phrase was left alone because on this UI that IS
  // what a sold auction means. Until R60 (2026-10-30) this header was one of a pair naming two
  // populations apart, the Opportunity header then explaining a rank band on other buyers' typical
  // bids; the band is gone from the list. The Opportunity cell's reasons quoted the row's P50 from
  // this population until R66 (2026-11-02), which left them a dash with no hover.
  return 'Where this campaign\'s bid sits among the prices auctions actually sold for in this'
    + ` market${windowPhrase}. The bar spans the middle half of those prices; the marker is the bid`
    + ' it was scored on.'
}

// What the Opportunity column holds (R60; owner, 2026-10-30): what the campaign can do, or why
// nothing is suggested. Each cell's own hover gives the detail, so the header names the kinds of
// cell and no window. Until R60 it explained a rank band, on a window, which the column no longer
// shows (docs/MARKET_CONTEXT_WIRING.md, "Tier vs rank band")
export function getOpportunityTooltip() {
  return 'What this campaign can do here: a suggested bid or setup, advice on its conversion rate,'
    + ' or why nothing is suggested.'
}


// Each fetch takes a ticket, and only the newest one may commit. Responses can arrive out of
// order - the queries run on ClickHouse and their latency varies with the size of the market
// cell - and the last response to arrive is not necessarily the one for what is on screen now.
let latestCampaignMarketPositionsRequest = 0
let latestCategoryMarketContextRequest = 0

const state = {
  // campaignId -> market position row, see MarketContextAPI.getCampaignMarketPositions
  campaignMarketPositions: {},
  // Length in hours of the window those rows were measured over: 24 or 168, straight off the
  // response envelope. Null until the first fetch resolves, which is what keeps the labels
  // bare rather than briefly wrong
  campaignMarketPositionsWindowHours: null,
  // The envelope's row cap was hit (more than `limit` market cells or bidding campaigns), so some
  // campaigns are missing from the rows or carry zeroed counts. They would otherwise render
  // exactly like a market with no auctions, and the missed-calls total would quietly undercount.
  campaignMarketPositionsTruncated: false,
  campaignMarketPositionsLimit: null,
  // The market panel's E2 response (BidMarketContextPanel, the flag-off fallback), null for an
  // empty cell, which keeps the panel hidden. Unchanged by market intelligence (I34).
  categoryMarketContext: null,
  // The card's copy of the same response (MarketIntelligenceCard, behind rx-market-intelligence):
  // whole, kept whenever the backend sends insufficientData, so an empty cell renders the card's
  // insufficient-data state (D3) instead of hiding. Separate state so the panel above stays as it is
  // (I34). Null until it loads, and for a response from a backend older than the card.
  marketIntelligenceContext: null,
  // The backend answered E2 without the card's fields (older than backend PR 1). Never cleared by a
  // fetch or a clear, only by the next answer that has them, so the budget step can fall back to the
  // panel for good rather than flip between the two while each one's fetch clears the other's state
  marketIntelligenceUnavailable: false
}

const getters = {
  getMarketPositionForCampaign: (state) => (campaignId) => {
    return state.campaignMarketPositions[campaignId] || null
  },

  campaignMarketPositionsWindowHours: (state) => state.campaignMarketPositionsWindowHours,

  campaignMarketPositionsTruncated: (state) => state.campaignMarketPositionsTruncated,

  campaignMarketPositionsLimit: (state) => state.campaignMarketPositionsLimit,

  // Whether the rows come from backend PR 1 on, which positions each one at its market's standard
  // minimum, on E1's window or, when that window is thin there, on the last 7 days (§3.7). Such a
  // row carries the window it used, positionWindowHours; an older backend's rows do not.
  campaignMarketPositionsHavePositionWindow: (state) =>
    Object.values(state.campaignMarketPositions).some(position => typeof position?.positionWindowHours === 'number'),

  // Whether the signed-in user is shown market data at all. A softphone agent is not: they take
  // calls and do not set the account's bids, so the list's market columns and banner and the
  // budget step's market panel are hidden from them, and nothing here is even fetched for them.
  // Staff impersonating the account keep it, as they keep every column. The kill switch off makes
  // an agent an ordinary buyer, who sees it again. Applied in ListBuyerCampaigns and Budget.
  //
  // A UI rule only - the endpoints still answer an agent's token. See Product Requirements #5 in
  // docs/MARKET_CONTEXT_WIRING.md.
  canViewMarketContext: (state, getters, rootState, rootGetters) =>
    rootGetters['globalSettings/isSoftphoneUser'] !== true || rootGetters['auth/isImpersonating'] === true
}

const actions = {
  async fetchCampaignMarketPositions({ commit, rootGetters }) {
    const request = ++latestCampaignMarketPositionsRequest

    let response
    try {
      response = await MarketContextAPI.getCampaignMarketPositions(
        rootGetters['auth/accountDetails']?.id
      )
    } catch (error) {
      // A newer fetch is already on its way, and that one decides what the buyer hears
      if (request !== latestCampaignMarketPositionsRequest) return
      throw error
    }

    // An inline bid edit refetches, so two of these can overlap; an older response landing last
    // would put back the tier of a bid the campaign no longer has
    if (request !== latestCampaignMarketPositionsRequest) return

    // {rows, truncated, limit, windowHours} envelope; the endpoint returns a row for every
    // campaign itself, whatever its routing type, so there is no campaignIds param anymore.
    // Rows and window go in on ONE commit so no render can ever pair rows from one window
    // with another window's label
    commit('SET_CAMPAIGN_MARKET_POSITIONS', {
      rows: response.data.rows,
      windowHours: response.data.windowHours,
      truncated: response.data.truncated,
      limit: response.data.limit
    })
  },

  clearCategoryMarketContext({ commit }) {
    // Also voids any fetch still in flight, which would otherwise land on the cleared panel
    latestCategoryMarketContextRequest++
    commit('SET_CATEGORY_MARKET_CONTEXT', null)
    commit('SET_MARKET_INTELLIGENCE_CONTEXT', null)
  },

  async fetchCategoryMarketContext({ commit, rootGetters }, { categoryId, countryId, languageId }) {
    const request = ++latestCategoryMarketContextRequest

    // Clear first so a failed fetch never leaves the previous category's numbers on screen
    // under the new category's name. Clearing alone does not cover a slow fetch: that is the
    // ticket check below.
    commit('SET_CATEGORY_MARKET_CONTEXT', null)
    commit('SET_MARKET_INTELLIGENCE_CONTEXT', null)

    let response
    try {
      response = await MarketContextAPI.getCategoryMarketContext(
        rootGetters['auth/accountDetails']?.id,
        { categoryId, countryId, languageId }
      )
    } catch (error) {
      // The buyer has already moved off this market, so its failure is not theirs to hear about
      if (request !== latestCategoryMarketContextRequest) return
      throw error
    }

    // The buyer changed category, country or language (or blanked one) while this was in
    // flight. Committing it would show the old market's prices, and its "Use $X" bids, under
    // the new category's name.
    if (request !== latestCategoryMarketContextRequest) return

    // An empty market cell comes back with null percentiles (tiers: [],
    // winRateCurve: null) - commit null so the panel's v-if keeps it hidden
    const isEmptyMarket = response.data?.winningBidP25InUSD == null
    commit('SET_CATEGORY_MARKET_CONTEXT', isEmptyMarket ? null : response.data)

    // The card keeps the whole response whenever the backend sends insufficientData, an empty cell
    // included: it shows that state rather than hide (D3). One fetch feeds both, and only one of the
    // panel and the card is ever on screen to ask for it.
    const hasCardFields = hasMarketIntelligence(response.data)
    commit('SET_MARKET_INTELLIGENCE_CONTEXT', hasCardFields ? response.data : null)
    commit('SET_MARKET_INTELLIGENCE_UNAVAILABLE', !hasCardFields)
  }
}

const mutations = {
  SET_CAMPAIGN_MARKET_POSITIONS(state, { rows, windowHours, truncated, limit }) {
    // Replace the whole map so reactivity works without Vue.set (kept Vue 3 compatible)
    state.campaignMarketPositions = rows.reduce((map, position) => {
      map[position.campaignId] = position
      return map
    }, {})

    // Committed even when rows is empty - an empty response is exactly the state the window
    // label exists to explain ("no bids in the last 24 hours" reads very differently from
    // "no bids in the last 7 days")
    state.campaignMarketPositionsWindowHours = windowHours ?? null

    state.campaignMarketPositionsTruncated = truncated === true
    state.campaignMarketPositionsLimit = limit ?? null
  },

  SET_CATEGORY_MARKET_CONTEXT(state, context) {
    state.categoryMarketContext = context
  },

  SET_MARKET_INTELLIGENCE_CONTEXT(state, context) {
    state.marketIntelligenceContext = context
  },

  SET_MARKET_INTELLIGENCE_UNAVAILABLE(state, unavailable) {
    state.marketIntelligenceUnavailable = unavailable === true
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
